import React from 'react';
import { useLocation, useNavigate, useSearchParams, matchPath } from 'react-router-dom';
import {useNavigateBack, getLocationReturnPath } from '../../../hooks/useNavigateBack';
import useSWR from 'swr';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { fetcher } from '../../../services/fetcher';
import { createSpareInvoice, updateSpareInvoice } from '../../../services/mutations';
import {
  Accordion,
  AccordionItem,
  Button,
  Callout,
  Card,
  ChevronLeftIcon,
  DateInput,
  Label,
  NumberInput,
  PlusIcon,
  RefreshIcon,
  Select,
  Textarea,
  TextInput,
  TrashIcon,
} from '../../../components/ui';

import { moduleConfig } from './spareInvoicesModuleConfig';
import { SpareInvoiceExpense, SpareInvoiceLine, useSpareInvoice } from './useData';
import { formatCaseParenthetical } from '../requisitions/caseDisplay';

type LineForm = {
  key: string;
  level1Filter: string;
  itemId: string;
  itemVcode: string;
  itemDescr: string;
  unitId: string;
  invoiceQty: string;
  unitPrice: string;
  unitTotal: string;
  discount: string;
  vat: string;
  lineTotal: string;
  itemNotes: string;
};

const newLine = (): LineForm => ({
  key: `line-${Date.now()}-${Math.random()}`,
  level1Filter: '',
  itemId: '',
  itemVcode: '',
  itemDescr: '',
  unitId: '',
  invoiceQty: '',
  unitPrice: '',
  unitTotal: '',
  discount: '',
  vat: '',
  lineTotal: '',
  itemNotes: '',
});

type ExpenseForm = {
  key: string;
  itemDescr: string;
  value: string;
  vat: string;
};

const newExpense = (): ExpenseForm => ({
  key: `exp-${Date.now()}-${Math.random()}`,
  itemDescr: '',
  value: '',
  vat: '',
});

const asNumber = (value: string): number | undefined => {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
};
const asPositiveInt = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? Math.floor(value) : undefined;
  }
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
  }
  return undefined;
};

const toFixed2String = (value: number): string => value.toFixed(2);
const normalizeTo2Dp = (value: string): string => {
  if (!value || value.trim() === '') return '';
  const n = Number(value);
  return Number.isFinite(n) ? toFixed2String(n) : value;
};
const computeUnitTotal = (qty: string, unitPrice: string): string => {
  const q = Number(qty);
  const p = Number(unitPrice);
  if (!Number.isFinite(q) || !Number.isFinite(p)) return '';
  const total = q * p;
  if (!Number.isFinite(total)) return '';
  return toFixed2String(total);
};
const computeFinalTotal = (unitTotal: string, discount: string, vat: string): string => {
  const u = Number(unitTotal);
  const d = Number(discount || '0');
  const v = Number(vat || '0');
  if (!Number.isFinite(u) || !Number.isFinite(d) || !Number.isFinite(v)) return '';
  const total = u - d + v;
  if (!Number.isFinite(total)) return '';
  return toFixed2String(total);
};
const computeCurrencyTotal = (lineTotal: string, rate: string): string => {
  const t = Number(lineTotal);
  const r = Number(rate);
  if (!Number.isFinite(t) || !Number.isFinite(r)) return '';
  const total = t * r;
  if (!Number.isFinite(total)) return '';
  return toFixed2String(total);
};
const asFinite = (value: string): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};
const fmt2 = (n: number): string =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const Form: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useNavigateBack(moduleConfig.module.route.list, getLocationReturnPath(location.state));
  const [searchParams] = useSearchParams();
  const editMatch = matchPath({ path: '/spares/spare-invoices/:id/edit', end: true }, location.pathname);
  const isEdit = editMatch != null;
  const rawId = editMatch?.params.id;
  const invoiceId = rawId != null && /^\d+$/.test(String(rawId)) ? Number(rawId) : undefined;
  const paramPoId = React.useMemo(() => {
    const e = searchParams.get('purchaseOrderId');
    const n = e != null ? Number(e) : NaN;
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
  }, [searchParams]);
  const paramDeliveryId = React.useMemo(() => {
    const e = searchParams.get('deliveryId');
    const n = e != null ? Number(e) : NaN;
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
  }, [searchParams]);

  const { data: existing, isLoading: loadingExisting } = useSpareInvoice(invoiceId);
  const { data: units = [] } = useSWR<any[]>('/spare-items', fetcher);
  const { data: posRes } = useSWR<any>('/spare-purchase-orders?page=1&limit=500', fetcher);
  const { data: currenciesRes } = useSWR<any>('/currencies?page=1&limit=500', fetcher);
  const { data: deliveriesRes } = useSWR<any>('/spare-deliveries?page=1&limit=500', fetcher);
  const { data: vesselsRes } = useSWR<any>('/vessels?page=1&limit=200', fetcher);
  const purchaseOrders = React.useMemo(
    () => (Array.isArray(posRes) ? posRes : posRes?.data ?? []),
    [posRes],
  );

  const [selectedPoId, setSelectedPoId] = React.useState('');
  const { data: selectedPo } = useSWR<any>(selectedPoId ? `/spare-purchase-orders/${selectedPoId}` : null, fetcher);

  const [isSaving, setIsSaving] = React.useState(false);
  const [formData, setFormData] = React.useState({
    invDate: '',
    ref: '',
    notes: '',
    exchangeRate: '',
    exchangeDate: '',
    paymentTerms: '',
    refN: '',
    rcvDate: '',
    dueDate: '',
    deliveryId: '',
    vesselId: '',
    currencyId: '',
  });
  const [lines, setLines] = React.useState<LineForm[]>([newLine()]);
  const [expenses, setExpenses] = React.useState<ExpenseForm[]>([newExpense()]);
  const [bulkDiscountPct, setBulkDiscountPct] = React.useState('');
  const [bulkVatPct, setBulkVatPct] = React.useState('');

  const { data: itemsRes } = useSWR<any>('/spare-items?page=1&limit=200', fetcher);
  const items = React.useMemo(
    () => (Array.isArray(itemsRes) ? itemsRes : itemsRes?.data ?? []),
    [itemsRes],
  );
  const selectedLevel1Values = React.useMemo(
    () =>
      Array.from(
        new Set(
          lines
            .map((line) => line.level1Filter?.trim())
            .filter((value): value is string => Boolean(value)),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [lines],
  );
  const { data: levelItemsMap = {} } = useSWR<Record<string, any[]>>(
    selectedLevel1Values.length > 0 ? ['invoice-line-level-items', ...selectedLevel1Values] : null,
    async () => {
      const entries = await Promise.all(
        selectedLevel1Values.map(async (level1) => {
          const params = new URLSearchParams();
          params.set('filters', JSON.stringify({ filters: { level1: [level1] } }));
          const response = await fetcher(`/spare-items?${params.toString()}`);
          const levelItems = Array.isArray(response) ? response : response?.data ?? [];
          return [level1, levelItems] as const;
        }),
      );
      return Object.fromEntries(entries);
    },
    { revalidateOnFocus: false },
  );
  const itemsById = React.useMemo(() => {
    const map = new Map<string, any>();
    for (const item of items) {
      map.set(String(item.id), item);
    }
    for (const levelItems of Object.values(levelItemsMap)) {
      for (const item of levelItems) {
        map.set(String(item.id), item);
      }
    }
    return map;
  }, [items, levelItemsMap]);
  const level1Options = React.useMemo(() => {
    const set = new Set<string>();
    for (const it of items) {
      const L = (it.level1 ?? '').trim();
      if (L) set.add(L);
    }
    return [
      { value: '', label: 'All' },
      ...Array.from(set)
        .sort((a, b) => a.localeCompare(b))
        .map((v) => ({ value: v, label: v })),
    ];
  }, [items]);

  const poOptions = React.useMemo(() => {
    const rows = purchaseOrders.map((p: any) => ({
      value: String(p.id),
      label: `${p.ref || `PO-${p.id}`}${p.supplier?.name ? ` · ${p.supplier.name}` : ''}`,
    }));
    if (existing?.purchaseOrderId && existing?.order && !rows.some((o: any) => o.value === String(existing.purchaseOrderId))) {
      rows.push({ value: String(existing.purchaseOrderId), label: existing.order.ref || `PO-${existing.purchaseOrderId}` });
    }
    return [{ value: '', label: 'Select purchase order' }, ...rows];
  }, [purchaseOrders, existing?.purchaseOrderId, existing?.order]);

  const currencies = React.useMemo(
    () =>
      (Array.isArray(currenciesRes) ? currenciesRes : currenciesRes?.data ?? []) as {
        id: number;
        code: string;
        name: string;
        symbol?: string | null;
        isActive?: boolean;
      }[],
    [currenciesRes],
  );

  const currencyOptions = React.useMemo(() => {
    const active = currencies.filter((c) => c.isActive !== false);
    const rows = active.map((c) => ({
      value: String(c.id),
      label: c.name,
    }));
    if (existing?.currencyId && existing?.currency && !rows.some((o) => o.value === String(existing.currencyId))) {
      rows.push({
        value: String(existing.currencyId),
        label: existing.currency.name,
      });
    }
    return [{ value: '', label: 'Currency (optional)' }, ...rows];
  }, [currencies, existing?.currency, existing?.currencyId]);
  const currencyTotalCaption = React.useMemo(() => {
    const selectedId = formData.currencyId ? Number(formData.currencyId) : NaN;
    const selected = Number.isFinite(selectedId) ? currencies.find((c) => c.id === selectedId) : undefined;
    const symbol = (selected?.symbol ?? existing?.currency?.symbol ?? '').trim();
    return symbol ? `(${symbol}) Total` : 'Total';
  }, [currencies, formData.currencyId, existing?.currency?.symbol]);

  const deliveries = React.useMemo(
    () => (Array.isArray(deliveriesRes) ? deliveriesRes : deliveriesRes?.data ?? []),
    [deliveriesRes],
  );
  const deliveryOptions = React.useMemo(() => {
    const n = asNumber(selectedPoId);
    const filtered = n
      ? deliveries.filter((d: any) => d.purchaseOrderId == null || Number(d.purchaseOrderId) === n)
      : [];
    const rows = filtered.map((d: any) => ({
      value: String(d.id),
      label: d.ref || `DLV-${d.id}`,
    }));
    if (existing?.deliveryId && !rows.some((o: { value: string }) => o.value === String(existing.deliveryId))) {
      rows.push({ value: String(existing.deliveryId), label: `Delivery #${existing.deliveryId}` });
    }
    return [{ value: '', label: 'Delivery (optional)' }, ...rows];
  }, [deliveries, selectedPoId, existing?.deliveryId]);

  const vessels = React.useMemo(
    () => (Array.isArray(vesselsRes) ? vesselsRes : vesselsRes?.data ?? []),
    [vesselsRes],
  );
  const vesselOptions = React.useMemo(() => {
    const rows = vessels.map((v: any) => ({
      value: String(v.id),
      label: v.vesselName || v.vesselAbrv || `Vessel #${v.id}`,
    }));
    if (existing?.vesselId && !rows.some((o: { value: string }) => o.value === String(existing.vesselId))) {
      rows.push({ value: String(existing.vesselId), label: `Vessel #${existing.vesselId}` });
    }
    return [{ value: '', label: 'Vessel (optional)' }, ...rows];
  }, [vessels, existing?.vesselId]);

  const getItemOptionsForLine = React.useCallback(
    (line: LineForm) => {
      const f = line.level1Filter?.trim();
      const filtered = f ? levelItemsMap[f] ?? [] : items;
      const rows: { value: string; label: string }[] = filtered.map((it: any) => ({
        value: String(it.id),
        label: `${it.vendorCode || ''} - ${it.name || ''}`,
      }));
      if (line.itemId && !rows.some((o) => o.value === line.itemId)) {
        const idStr = String(line.itemId);
        const label =
          (line.itemVcode && line.itemVcode.trim()) || (line.itemDescr && line.itemDescr.trim())
            ? `${(line.itemVcode || '').trim()} - ${(line.itemDescr || '').trim()}`
            : `Item #${idStr}`;
        rows.push({ value: idStr, label });
      }
      return [{ value: '', label: 'Select item' }, ...rows];
    },
    [items, levelItemsMap],
  );

  const unitOptions = React.useMemo(() => {
    const rows: { value: string; label: string }[] = units.map((unit) => ({
      value: String(unit.id),
      label: unit.ucode || unit.descr || `Unit #${unit.id}`,
    }));
    const seen = new Set(rows.map((o) => o.value));
    for (const line of lines) {
      if (!String(line.unitId).trim()) continue;
      const uStr = String(line.unitId);
      if (seen.has(uStr)) continue;
      seen.add(uStr);
      rows.push({ value: uStr, label: `Unit #${uStr}` });
    }
    return [{ value: '', label: 'Unit' }, ...rows];
  }, [units, lines]);

  React.useEffect(() => {
    if (!isEdit && paramPoId) setSelectedPoId(String(paramPoId));
  }, [isEdit, paramPoId]);

  React.useEffect(() => {
    if (isEdit || !paramDeliveryId) return;
    setFormData((s) => ({ ...s, deliveryId: String(paramDeliveryId) }));
  }, [isEdit, paramDeliveryId]);

  React.useEffect(() => {
    if (!existing) return;
    const fallbackDelivery = deliveries.find((d: any) => Number(d?.id) === Number(existing.deliveryId));
    const resolvedVesselId =
      asPositiveInt(existing.vesselId) ??
      asPositiveInt((existing as any)?.delivery?.vesselId) ??
      asPositiveInt((existing as any)?.delivery?.vessel?.id) ??
      asPositiveInt(fallbackDelivery?.vesselId);
    setSelectedPoId(existing.purchaseOrderId != null ? String(existing.purchaseOrderId) : '');
    setFormData({
      invDate: existing.invDate ? existing.invDate.slice(0, 10) : '',
      ref: existing.ref || '',
      notes: existing.notes || '',
      exchangeRate: existing.exchangeRate != null ? String(existing.exchangeRate) : '',
      exchangeDate: existing.exchangeDate ? existing.exchangeDate.slice(0, 10) : '',
      paymentTerms: existing.paymentTerms || '',
      refN: existing.refN != null ? String(existing.refN) : '',
      rcvDate: existing.rcvDate ? existing.rcvDate.slice(0, 10) : '',
      dueDate: existing.dueDate ? existing.dueDate.slice(0, 10) : '',
      deliveryId: existing.deliveryId != null ? String(existing.deliveryId) : '',
      vesselId: resolvedVesselId != null ? String(resolvedVesselId) : '',
      currencyId: existing.currencyId != null ? String(existing.currencyId) : '',
    });
    const mapped = (existing.lineItems || []).map((line) => ({
      key: `line-${line.id || Math.random()}`,
      level1Filter: (line.item?.level1 ?? '').trim(),
      itemId: line.itemId ? String(line.itemId) : '',
      itemVcode: line.itemVcode || '',
      itemDescr: line.itemDescr || '',
      unitId: line.unitId ? String(line.unitId) : '',
      invoiceQty: line.invoiceQty != null ? String(line.invoiceQty) : '',
      unitPrice: line.unitPrice != null ? String(line.unitPrice) : '',
      unitTotal: computeUnitTotal(
        line.invoiceQty != null ? String(line.invoiceQty) : '',
        line.unitPrice != null ? String(line.unitPrice) : '',
      ),
      lineTotal:
        line.lineTotal != null
          ? String(line.lineTotal)
          : computeFinalTotal(
              computeUnitTotal(
                line.invoiceQty != null ? String(line.invoiceQty) : '',
                line.unitPrice != null ? String(line.unitPrice) : '',
              ),
              line.discount != null ? String(line.discount) : '',
              line.vat != null ? String(line.vat) : '',
            ),
      discount: line.discount != null ? String(line.discount) : '',
      vat: line.vat != null ? String(line.vat) : '',
      itemNotes: line.itemNotes || '',
    }));
    setLines(mapped.length > 0 ? mapped : [newLine()]);
    const expMapped = (existing.expenses || []).map((exp) => ({
      key: `exp-${exp.id || Math.random()}`,
      itemDescr: exp.itemDescr || '',
      value: exp.value != null ? String(exp.value) : '',
      vat: exp.vat != null ? String(exp.vat) : '',
    }));
    setExpenses(expMapped.length > 0 ? expMapped : [newExpense()]);
  }, [existing, deliveries]);

  React.useEffect(() => {
    if (!formData.deliveryId || formData.vesselId) return;
    const selectedDelivery = deliveries.find((d: any) => String(d?.id) === String(formData.deliveryId));
    const deliveryVesselId = asPositiveInt(selectedDelivery?.vesselId);
    if (!deliveryVesselId) return;
    setFormData((s) => (s.vesselId ? s : { ...s, vesselId: String(deliveryVesselId) }));
  }, [deliveries, formData.deliveryId, formData.vesselId]);

  const copyLinesFromPo = React.useCallback(() => {
    if (!selectedPo || !Array.isArray(selectedPo.lineItems)) return;
    const mapped = selectedPo.lineItems.map((line: any) => ({
      key: `line-${line.id || Math.random()}`,
      level1Filter: (line.item?.level1 ?? '').trim(),
      itemId: line.itemId ? String(line.itemId) : '',
      itemVcode: line.itemVcode || '',
      itemDescr: line.itemDescr || '',
      unitId: line.unitId ? String(line.unitId) : '',
      invoiceQty: line.qty != null ? String(line.qty) : '',
      unitPrice: line.unitPrice != null ? String(line.unitPrice) : '',
      unitTotal: computeUnitTotal(
        line.qty != null ? String(line.qty) : '',
        line.unitPrice != null ? String(line.unitPrice) : '',
      ),
      lineTotal:
        line.lineTotal != null
          ? String(line.lineTotal)
          : computeFinalTotal(
              computeUnitTotal(
                line.qty != null ? String(line.qty) : '',
                line.unitPrice != null ? String(line.unitPrice) : '',
              ),
              line.discount != null ? String(line.discount) : '',
              line.vat != null ? String(line.vat) : '',
            ),
      discount: line.discount != null ? String(line.discount) : '',
      vat: line.vat != null ? String(line.vat) : '',
      itemNotes: line.itemNotes || '',
    }));
    setLines(mapped.length > 0 ? mapped : [newLine()]);
  }, [selectedPo]);
  const autoCopiedPoIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (isEdit) return;
    if (!selectedPoId) {
      autoCopiedPoIdRef.current = null;
      return;
    }
    if (autoCopiedPoIdRef.current === selectedPoId) return;
    if (!selectedPo || !Array.isArray(selectedPo.lineItems)) return;
    copyLinesFromPo();
    autoCopiedPoIdRef.current = selectedPoId;
  }, [isEdit, selectedPoId, selectedPo, copyLinesFromPo]);

  const onItemChange = (lineKey: string, itemId: string) => {
    const item = itemsById.get(itemId);
    setLines((prev) =>
      prev.map((line) =>
        line.key !== lineKey
          ? line
          : {
              ...line,
              itemId,
              itemVcode: item?.vendorCode || '',
              itemDescr: item?.name || '',
              unitId: item?.unitId ? String(item.unitId) : '',
              level1Filter: (item?.level1 ?? '').trim(),
              unitTotal: '',
              discount: '',
              vat: '',
              lineTotal: '',
            },
      ),
    );
  };
  const onLevel1FilterChange = (lineKey: string, value: string) => {
    setLines((prev) =>
      prev.map((line) => {
        if (line.key !== lineKey) return line;
        const next: LineForm = { ...line, level1Filter: value };
        const f = value.trim();
        const previousLevel1 = (line.level1Filter ?? '').trim();
        if (f && line.itemId && previousLevel1 !== f) {
          next.itemId = '';
          next.itemDescr = '';
          next.itemVcode = '';
          next.unitId = '';
        }
        return next;
      }),
    );
  };
  React.useEffect(() => {
    if (!itemsById.size) return;
    setLines((prev) => {
      let changed = false;
      const next = prev.map((line) => {
        if (!line.itemId || line.level1Filter?.trim()) return line;
        const item = itemsById.get(line.itemId);
        if (!item) return line;
        changed = true;
        return { ...line, level1Filter: (item.level1 ?? '').trim() };
      });
      return changed ? next : prev;
    });
  }, [itemsById, invoiceId]);

  const normalizedLines: SpareInvoiceLine[] = React.useMemo(() => {
    return lines
      .map((line) => ({
        itemId: asNumber(line.itemId),
        itemVcode: line.itemVcode || undefined,
        itemDescr: line.itemDescr || undefined,
        unitId: asNumber(line.unitId),
        invoiceQty: asNumber(line.invoiceQty),
        unitPrice: asNumber(line.unitPrice),
        lineTotal: asNumber(line.lineTotal),
        discount: asNumber(line.discount),
        vat: asNumber(line.vat),
        itemNotes: line.itemNotes || undefined,
      }))
      .filter((line) => line.itemId || line.itemDescr || line.invoiceQty != null || line.itemNotes || line.discount != null || line.vat != null);
  }, [lines]);

  const lineSums = React.useMemo(() => {
    return lines.reduce(
      (acc, line) => {
        acc.unitTotal += asFinite(line.unitTotal);
        acc.discount += asFinite(line.discount);
        acc.vat += asFinite(line.vat);
        acc.total += asFinite(line.lineTotal);
        acc.currencyTotal += asFinite(computeCurrencyTotal(line.lineTotal, formData.exchangeRate));
        return acc;
      },
      { unitTotal: 0, discount: 0, vat: 0, total: 0, currencyTotal: 0 },
    );
  }, [lines, formData.exchangeRate]);

  const normalizedExpenses: SpareInvoiceExpense[] = React.useMemo(() => {
    return expenses
      .map((row) => ({
        itemDescr: row.itemDescr.trim() || undefined,
        value: asNumber(row.value),
        vat: asNumber(row.vat),
      }))
      .filter((row) => row.itemDescr || row.value != null || row.vat != null);
  }, [expenses]);

  const submit = async () => {
    const poId = asNumber(selectedPoId);
    const hasRequiredDetails =
      Boolean(formData.invDate.trim()) &&
      Boolean(formData.ref.trim()) &&
      Boolean(formData.currencyId.trim()) &&
      asNumber(formData.exchangeRate) != null;
    if (!poId || !hasRequiredDetails) return;
    setIsSaving(true);
    try {
      const payload = {
        invDate: formData.invDate || undefined,
        purchaseOrderId: poId,
        ref: formData.ref || undefined,
        notes: formData.notes || undefined,
        exchangeRate: asNumber(formData.exchangeRate),
        exchangeDate: formData.exchangeDate || undefined,
        paymentTerms: formData.paymentTerms || undefined,
        refN: asNumber(formData.refN),
        rcvDate: formData.rcvDate || undefined,
        dueDate: formData.dueDate || undefined,
        deliveryId: asNumber(formData.deliveryId),
        vesselId: asNumber(formData.vesselId),
        currencyId: asNumber(formData.currencyId),
        lineItems: normalizedLines,
        expenses: normalizedExpenses,
      };
      if (isEdit && invoiceId) await updateSpareInvoice(invoiceId, payload);
      else await createSpareInvoice(payload);
      goBack();
    } finally {
      setIsSaving(false);
    }
  };

  
  const hasRequiredInvoiceDetails =
    Boolean(formData.invDate.trim()) &&
    Boolean(formData.ref.trim()) &&
    Boolean(formData.currencyId.trim()) &&
    asNumber(formData.exchangeRate) != null;
  const invoiceCaseCaption = React.useMemo(() => {
    if (!isEdit) return null;
    const caseIdN = existing?.caseId ?? null;
    if (caseIdN == null || caseIdN <= 0) return null;
    return (
      formatCaseParenthetical(
        existing?.spareCase ?? {
          id: caseIdN,
        },
        (iso) => formatDateGlobal(iso || undefined),
      ) ?? null
    );
  }, [isEdit, existing?.caseId, existing?.spareCase]);
  const invoicePurchaseOrderCaption = React.useMemo(() => {
    if (!isEdit) return null;
    const ref = existing?.order?.ref?.trim() || (existing?.purchaseOrderId != null ? `PO-${existing.purchaseOrderId}` : '');
    if (!ref) return null;
    return `Purchase order: ${ref}`;
  }, [isEdit, existing?.order?.ref, existing?.purchaseOrderId]);
  const invoiceDeliveryCaption = React.useMemo(() => {
    if (!isEdit) return null;
    const ref = (existing as any)?.delivery?.ref?.trim() || (existing?.deliveryId != null ? `DLV-${existing.deliveryId}` : '');
    if (!ref) return null;
    return `Delivery: ${ref}`;
  }, [isEdit, existing?.deliveryId, (existing as any)?.delivery?.ref]);
  const invoiceVesselCaption = React.useMemo(() => {
    if (!isEdit) return null;
    const vesselName = (existing as any)?.vessel?.vesselName?.trim() || '';
    if (vesselName) return `Vessel: ${vesselName}`;
    if (existing?.vesselId != null) return `Vessel: #${existing.vesselId}`;
    return null;
  }, [isEdit, (existing as any)?.vessel?.vesselName, existing?.vesselId]);
  const shouldLockOrderLineFields = React.useCallback(() => {
    const caseIdN = existing?.caseId ?? null;
    const orderIdN = asNumber(selectedPoId);
    return caseIdN != null && caseIdN > 0 && orderIdN != null && orderIdN > 0;
  }, [existing?.caseId, selectedPoId]);

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <Card
        variant="outlined"
        className={cn('flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-3.5', 'border shadow-sm')}
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('-ml-2 gap-1.5 font-medium', colors.text)}
          onClick={goBack}
        >
          <ChevronLeftIcon className="h-4 w-4 opacity-80" />
          Back
        </Button>
        <Button
          type="submit"
          disabled={isSaving || loadingExisting || !asNumber(selectedPoId) || !hasRequiredInvoiceDetails}
          loading={isSaving}
          size="default"
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </Card>

      {invoiceCaseCaption ? (
        <Callout variant="success" title="Linked spare case">
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span
                  className={cn(
                    'inline-flex items-center rounded-lg border px-2.5 py-1 text-sm',
                    'border-green-200/90 bg-green-100/70 text-green-950 dark:border-green-700/60 dark:bg-green-900/40 dark:text-green-100',
                  )}
                >
                  {invoiceCaseCaption}
                </span>
                {invoicePurchaseOrderCaption ? (
                  <span
                    className={cn(
                      'inline-flex items-center rounded-lg border px-2.5 py-1 text-sm font-medium',
                      'border-indigo-200/90 bg-indigo-50/90 text-indigo-900 dark:border-indigo-700/60 dark:bg-indigo-950/40 dark:text-indigo-200',
                    )}
                  >
                    {invoicePurchaseOrderCaption}
                  </span>
                ) : null}
                {invoiceDeliveryCaption ? (
                  <span
                    className={cn(
                      'inline-flex items-center rounded-lg border px-2.5 py-1 text-sm font-medium',
                      'border-indigo-200/90 bg-indigo-50/90 text-indigo-900 dark:border-indigo-700/60 dark:bg-indigo-950/40 dark:text-indigo-200',
                    )}
                  >
                    {invoiceDeliveryCaption}
                  </span>
                ) : null}
                {invoiceVesselCaption ? (
                  <span
                    className={cn(
                      'inline-flex items-center rounded-lg border px-2.5 py-1 text-sm font-medium',
                      'border-indigo-200/90 bg-indigo-50/90 text-indigo-900 dark:border-indigo-700/60 dark:bg-indigo-950/40 dark:text-indigo-200',
                    )}
                  >
                    {invoiceVesselCaption}
                  </span>
                ) : null}
              </div>
            </Callout>
      ) : null}

      <Card variant="default" className="p-4 md:p-5">
        <div className="mb-4">
          <h2 className={cn('text-sm font-semibold uppercase tracking-wide', colors.textSecondary)}>Invoice details</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
          <div className="md:col-span-2">
            <Label htmlFor="inv-date" required>Invoice date</Label>
            <DateInput
              id="inv-date"
              className="h-9 min-h-[2.25rem]"
              value={formData.invDate}
              onChange={(e) => setFormData((s) => ({ ...s, invDate: e.target.value }))}
              placeholder="Select date"
              showFormatHint={false}
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="inv-due">Due</Label>
            <DateInput
              id="inv-due"
              className="h-9 min-h-[2.25rem]"
              value={formData.dueDate}
              onChange={(e) => setFormData((s) => ({ ...s, dueDate: e.target.value }))}
              placeholder="Select date"
              showFormatHint={false}
            />
          </div>
          {!isEdit ? (
            <div className="md:col-span-3">
              <Label htmlFor="inv-po">Purchase order</Label>
              <Select
                id="inv-po"
                value={selectedPoId}
                onChange={(e) => setSelectedPoId(e.target.value)}
                options={poOptions}
                placeholder="Select purchase order"
                showClearButton={false}
              />
            </div>
          ) : null}
          <div className="md:col-span-2">
            <Label htmlFor="inv-ref" required>Reference</Label>
            <TextInput id="inv-ref" value={formData.ref} onChange={(e) => setFormData((s) => ({ ...s, ref: e.target.value }))} placeholder="Reference" />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="inv-currency" required>Currency</Label>
            <Select
              id="inv-currency"
              value={formData.currencyId}
              onChange={(e) => setFormData((s) => ({ ...s, currencyId: e.target.value }))}
              options={currencyOptions}
              showClearButton={false}
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="inv-ex-rate" required>Exchange rate</Label>
            <NumberInput
              id="inv-ex-rate"
              value={formData.exchangeRate}
              onChange={(e) => setFormData((s) => ({ ...s, exchangeRate: e.target.value == null ? '' : String(e.target.value) }))}
              showClearButton={false}
              placeholder="Rate"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="inv-ex-date">Exchange date</Label>
            <DateInput
              id="inv-ex-date"
              value={formData.exchangeDate}
              onChange={(e) => setFormData((s) => ({ ...s, exchangeDate: e.target.value }))}
              placeholder="Select date"
              showFormatHint={false}
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="inv-rcv">Received</Label>
            <DateInput
              id="inv-rcv"
              value={formData.rcvDate}
              onChange={(e) => setFormData((s) => ({ ...s, rcvDate: e.target.value }))}
              placeholder="Select date"
              showFormatHint={false}
            />
          </div>
          {!isEdit ? (
            <>
              <div className="md:col-span-3">
                <Label htmlFor="inv-dlv">Delivery</Label>
                <Select
                  id="inv-dlv"
                  value={formData.deliveryId}
                  onChange={(e) => setFormData((s) => ({ ...s, deliveryId: e.target.value }))}
                  options={deliveryOptions}
                  showClearButton={false}
                />
              </div>
              <div className="md:col-span-3">
                <Label htmlFor="inv-vessel">Vessel</Label>
                <Select
                  id="inv-vessel"
                  value={formData.vesselId}
                  onChange={(e) => setFormData((s) => ({ ...s, vesselId: e.target.value }))}
                  options={vesselOptions}
                  showClearButton={false}
                />
              </div>
            </>
          ) : null}
          <div className="md:col-span-5">
            <Label htmlFor="inv-payment-terms">Payment terms</Label>
            <TextInput
              id="inv-payment-terms"
              value={formData.paymentTerms}
              onChange={(e) => setFormData((s) => ({ ...s, paymentTerms: e.target.value }))}
              placeholder="Payment terms"
            />
          </div>
          <div className="md:col-span-5">
            <Label htmlFor="inv-notes">Notes</Label>
            <Textarea
              id="inv-notes"
              value={formData.notes}
              onChange={(e) => setFormData((s) => ({ ...s, notes: e.target.value }))}
              rows={2}
              showClearButton={false}
              className="min-h-[72px]"
            />
          </div>
        </div>
      </Card>

      <Card variant="default" className="p-4 md:p-5">
        <Accordion className="space-y-3">
          <AccordionItem
            title="Expenses"
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            <div className="mb-3 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="icon"
                title="Add expense"
                aria-label="Add expense"
                onClick={() => setExpenses((prev) => [...prev, newExpense()])}
              >
                <PlusIcon className="h-5 w-5" />
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className={cn('border-b', colors.border)}>
                    <th className="min-w-[10rem] px-2 py-2 text-left text-xs font-medium">Description</th>
                    <th className="w-28 px-2 py-2 text-left text-xs font-medium">Value</th>
                    <th className="w-28 px-2 py-2 text-left text-xs font-medium">VAT</th>
                    <th className="px-2 py-2 text-right text-xs font-medium"> </th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((row) => (
                    <tr key={row.key} className="align-top">
                      <td className="p-2">
                        <TextInput
                          size="sm"
                          value={row.itemDescr}
                          onChange={(e) =>
                            setExpenses((prev) => prev.map((r) => (r.key === row.key ? { ...r, itemDescr: e.target.value } : r)))
                          }
                          showClearButton={false}
                          placeholder="Description"
                        />
                      </td>
                      <td className="p-2">
                        <NumberInput
                          size="sm"
                          value={row.value}
                          onChange={(e) =>
                            setExpenses((prev) =>
                              prev.map((r) => (r.key === row.key ? { ...r, value: e.target.value == null ? '' : String(e.target.value) } : r)),
                            )
                          }
                          showClearButton={false}
                          placeholder="Value"
                        />
                      </td>
                      <td className="p-2">
                        <NumberInput
                          size="sm"
                          value={row.vat}
                          onChange={(e) =>
                            setExpenses((prev) =>
                              prev.map((r) => (r.key === row.key ? { ...r, vat: e.target.value == null ? '' : String(e.target.value) } : r)),
                            )
                          }
                          showClearButton={false}
                          placeholder="VAT"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Button
                          type="button"
                          variant="danger"
                          size="icon"
                          onClick={() => setExpenses((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== row.key) : [newExpense()]))}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AccordionItem>

          <AccordionItem
            title="Lines"
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <div className={cn('flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm', colors.border)}>
                  <span className={cn('text-xs font-medium', colors.textSecondary)}>Apply Item Discount (%)</span>
                  <NumberInput
                    size="sm"
                    value={bulkDiscountPct}
                    onChange={(e) => setBulkDiscountPct(e.target.value == null ? '' : String(e.target.value))}
                    placeholder="%"
                    showClearButton={false}
                    className="h-9 min-h-[2.25rem] w-24 box-border py-1.5 leading-normal"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const pct = Number(bulkDiscountPct);
                      if (!Number.isFinite(pct)) return;
                      setLines((prev) =>
                        prev.map((r) => {
                          const unit = Number(r.unitTotal);
                          const discountValue = Number.isFinite(unit) ? toFixed2String((unit * pct) / 100) : '';
                          return {
                            ...r,
                            discount: discountValue,
                            lineTotal: computeFinalTotal(r.unitTotal, discountValue, r.vat),
                          };
                        }),
                      );
                    }}
                    disabled={!lines.length}
                  >
                    Apply
                  </Button>
                </div>
                <div className={cn('flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm', colors.border)}>
                  <span className={cn('text-xs font-medium', colors.textSecondary)}>Apply Item VAT (%)</span>
                  <NumberInput
                    size="sm"
                    value={bulkVatPct}
                    onChange={(e) => setBulkVatPct(e.target.value == null ? '' : String(e.target.value))}
                    placeholder="%"
                    showClearButton={false}
                    className="h-9 min-h-[2.25rem] w-24 box-border py-1.5 leading-normal"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const pct = Number(bulkVatPct);
                      if (!Number.isFinite(pct)) return;
                      setLines((prev) =>
                        prev.map((r) => {
                          const unit = Number(r.unitTotal);
                          const vatValue = Number.isFinite(unit) ? toFixed2String((unit * pct) / 100) : '';
                          return {
                            ...r,
                            vat: vatValue,
                            lineTotal: computeFinalTotal(r.unitTotal, r.discount, vatValue),
                          };
                        }),
                      );
                    }}
                    disabled={!lines.length}
                  >
                    Apply
                  </Button>
                </div>
              </div>
              <Button type="button" variant="outline" size="icon" title="Add line" aria-label="Add line" onClick={() => setLines((prev) => [...prev, newLine()])}>
                <PlusIcon className="h-5 w-5" />
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className={cn('border-b', colors.border)}>
                    <th className="w-[10rem] min-w-[8rem] px-2 py-2 text-left text-xs font-medium">Level 1</th>
                    <th className="min-w-[12rem] px-2 py-2 text-left text-xs font-medium">Item</th>
                    <th className="w-[6.5rem] px-2 py-2 text-right text-xs font-medium">Unit</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">Qty</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">Price</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">Unit Total</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">Discount</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">VAT</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">Total</th>
                    <th className="w-28 px-2 py-2 text-right text-xs font-medium">{currencyTotalCaption}</th>
                    <th className="w-[12rem] px-2 py-2 text-left text-xs font-medium">Notes</th>
                    <th className="px-2 py-2 text-right text-xs font-medium"> </th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => {
                    const lockOrderFields = shouldLockOrderLineFields();
                    return (
                    <tr key={line.key} className="align-top">
                      <td className="p-2">
                        <Select
                          value={line.level1Filter}
                          onChange={(e) => onLevel1FilterChange(line.key, e.target.value)}
                          options={level1Options}
                          placeholder="Level 1"
                          showClearButton={false}
                          className="h-10 min-h-[2.5rem] w-full text-sm"
                          disabled={lockOrderFields}
                        />
                      </td>
                      <td className="p-2">
                        <Select
                          value={line.itemId}
                          onChange={(e) => onItemChange(line.key, e.target.value)}
                          options={getItemOptionsForLine(line)}
                          placeholder="Select item"
                          showClearButton={false}
                          className="h-10 min-h-[2.5rem] w-full min-w-[12rem] text-sm"
                          disabled={lockOrderFields}
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Select
                          value={line.unitId}
                          onChange={(e) => setLines((prev) => prev.map((r) => (r.key === line.key ? { ...r, unitId: e.target.value } : r)))}
                          options={unitOptions}
                          placeholder="Unit"
                          showClearButton={false}
                          className="h-9 min-h-[2.25rem] text-xs text-right"
                          disabled={lockOrderFields}
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={line.invoiceQty}
                          onChange={(e) =>
                            setLines((prev) =>
                              prev.map((r) => {
                                if (r.key !== line.key) return r;
                                const qtyValue = e.target.value == null ? '' : String(e.target.value);
                                const unitTotal = computeUnitTotal(qtyValue, r.unitPrice);
                                return {
                                  ...r,
                                  invoiceQty: qtyValue,
                                  unitTotal,
                                  lineTotal: computeFinalTotal(unitTotal, r.discount, r.vat),
                                };
                              }),
                            )
                          }
                          showClearButton={false}
                          placeholder="Qty"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={line.unitPrice}
                          onChange={(e) =>
                            setLines((prev) =>
                              prev.map((r) => {
                                if (r.key !== line.key) return r;
                                const priceValue = e.target.value == null ? '' : String(e.target.value);
                                const unitTotal = computeUnitTotal(r.invoiceQty, priceValue);
                                return {
                                  ...r,
                                  unitPrice: priceValue,
                                  unitTotal,
                                  lineTotal: computeFinalTotal(unitTotal, r.discount, r.vat),
                                };
                              }),
                            )
                          }
                          showClearButton={false}
                          placeholder="Price"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={line.unitTotal}
                          disabled
                          showClearButton={false}
                          placeholder="Unit Total"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={line.discount}
                          onChange={(e) =>
                            setLines((prev) =>
                              prev.map((r) => {
                                if (r.key !== line.key) return r;
                                const discountValue = e.target.value == null ? '' : String(e.target.value);
                                return {
                                  ...r,
                                  discount: discountValue,
                                  lineTotal: computeFinalTotal(r.unitTotal, discountValue, r.vat),
                                };
                              }),
                            )
                          }
                          showClearButton={false}
                          placeholder="Discount"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={line.vat}
                          onChange={(e) =>
                            setLines((prev) =>
                              prev.map((r) => {
                                if (r.key !== line.key) return r;
                                const vatValue = e.target.value == null ? '' : String(e.target.value);
                                return {
                                  ...r,
                                  vat: vatValue,
                                  lineTotal: computeFinalTotal(r.unitTotal, r.discount, vatValue),
                                };
                              }),
                            )
                          }
                          showClearButton={false}
                          placeholder="VAT"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={line.lineTotal}
                          disabled
                          showClearButton={false}
                          placeholder="Total"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={computeCurrencyTotal(line.lineTotal, formData.exchangeRate)}
                          disabled
                          showClearButton={false}
                          placeholder={currencyTotalCaption}
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2">
                        <TextInput
                          size="sm"
                          value={line.itemNotes}
                          onChange={(e) => setLines((prev) => prev.map((r) => (r.key === line.key ? { ...r, itemNotes: e.target.value } : r)))}
                          showClearButton={false}
                          placeholder="Notes"
                          className="h-10 min-h-[2.5rem]"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Button type="button" variant="danger" size="icon" onClick={() => setLines((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== line.key) : [newLine()]))}>
                          <TrashIcon className="h-5 w-5" />
                        </Button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className={cn('border-t', colors.border)}>
                    <td colSpan={5} className={cn('px-2 py-2 text-right text-xs font-semibold', colors.textSecondary)}>
                      SUM
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>Unit Total SUM</div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.unitTotal)}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>Discount SUM</div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.discount)}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>VAT SUM</div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.vat)}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>TOTAL SUM</div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.total)}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>{currencyTotalCaption} SUM</div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.currencyTotal)}</div>
                    </td>
                    <td />
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </AccordionItem>
        </Accordion>
      </Card>
    </form>
  );
};

export default Form;
